/**
 * BuildingSheet, bottom sheet for reporting something elsewhere in the building.
 *
 * Borrowed from YLM's "detailed report" flow but uses our Location tree. The
 * parent supplies a list of categories (electrical, plumbing, HVAC, …); on
 * pick, this fires onSubmit which the parent sends to /api/feedback with
 * mode=issue and scopeLocationId=<building root>.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { cn } from '@tendedloop/ui';

const CONFIRM_MS = 700;

export interface BuildingCategory {
  key: string;
  icon: string; // Emoji
  label: string;
  hint: string;
}

interface BuildingSheetProps {
  open: boolean;
  buildingName: string;
  categories: BuildingCategory[];
  onClose: () => void;
  onSubmit: (category: BuildingCategory) => void;
  className?: string;
}

export function BuildingSheet({ open, buildingName, categories, onClose, onSubmit, className }: BuildingSheetProps) {
  const { t } = useTranslation('visitor');
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setConfirmingKey(null);
  }, [open]);

  const handlePick = (c: BuildingCategory) => {
    if (confirmingKey) return;
    setConfirmingKey(c.key);
    onSubmit(c);
    setTimeout(() => onClose(), CONFIRM_MS);
  };

  return (
    <>
      <button
        type="button"
        aria-hidden={!open}
        tabIndex={-1}
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/55 transition-opacity',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="building-sheet-title"
        aria-hidden={!open}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 max-h-[80%] overflow-y-auto rounded-t-[32px] px-5 pb-8 pt-[18px]',
          'transition-transform duration-300 ease-out',
          'shadow-[0_-20px_60px_rgba(10,14,20,0.2)]',
          open ? 'translate-y-0' : 'translate-y-full',
          className,
        )}
        style={{ background: 'var(--dir-bg-paper, #fff)' }}
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-4 h-1 w-10 rounded-full"
          style={{ background: 'color-mix(in srgb, var(--dir-ink-muted) 28%, transparent)' }}
        />

        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div
              className="text-[15px] font-medium"
              style={{ color: 'var(--dir-ink-muted)', fontFamily: 'var(--dir-font-body)' }}
            >
              {t('v2.buildingSheet.scope', 'The whole building')}
            </div>
            <h3
              id="building-sheet-title"
              className="mt-1 text-[24px] font-bold leading-tight tracking-[-0.025em]"
              style={{
                color: 'var(--dir-ink-primary)',
                fontFamily: 'var(--dir-font-display)',
                fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
              }}
            >
              {buildingName}
            </h3>
            <p className="mt-2 text-[15px] font-medium" style={{ color: 'var(--dir-ink-muted)' }}>
              {t('v2.buildingSheet.intro', "Pick a category. It'll go to the right team, anonymous.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('v2.close', 'Close')}
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-all active:scale-95"
            style={{
              background: 'color-mix(in srgb, var(--dir-ink-muted) 12%, var(--dir-bg-paper))',
              color: 'var(--dir-ink-secondary)',
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-1 grid grid-cols-3 gap-2.5">
          {categories.map((c) => {
            const isConfirming = confirmingKey === c.key;
            const otherConfirming = !!confirmingKey && !isConfirming;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => handlePick(c)}
                disabled={!!confirmingKey}
                aria-live={isConfirming ? 'polite' : undefined}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-3.5',
                  'transition-all duration-200',
                  !confirmingKey && 'active:scale-[0.96]',
                  otherConfirming && 'opacity-40',
                )}
                style={
                  isConfirming
                    ? {
                        background: 'color-mix(in srgb, var(--dir-accent-primary) 10%, var(--dir-bg-paper))',
                        borderColor: 'color-mix(in srgb, var(--dir-accent-primary) 40%, transparent)',
                      }
                    : {
                        background: 'color-mix(in srgb, var(--dir-ink-muted) 8%, var(--dir-bg-paper))',
                        borderColor: 'color-mix(in srgb, var(--dir-ink-muted) 14%, var(--dir-bg-paper))',
                      }
                }
              >
                {isConfirming ? (
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 items-center justify-center rounded-full"
                    style={{ background: 'var(--dir-accent-primary)' }}
                  >
                    <Check className="h-4 w-4" strokeWidth={3} style={{ color: 'var(--dir-accent-on)' }} />
                  </span>
                ) : (
                  <span className="text-[26px]" aria-hidden="true">
                    {c.icon}
                  </span>
                )}
                <span
                  className="text-[15px] font-semibold"
                  style={{
                    color: isConfirming
                      ? 'color-mix(in srgb, var(--dir-accent-primary) 80%, var(--dir-ink-primary))'
                      : 'var(--dir-ink-primary)',
                    fontFamily: 'var(--dir-font-body)',
                  }}
                >
                  {isConfirming ? t('v2.issueSheet.logged', 'Logged') : c.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
